import { RawDraftContentBlock, RawDraftContentState, RawDraftInlineStyleRange } from 'draft-js';

/**
 * The AI commentary's text format, which the model writes and people edit:
 *
 *   # Headline
 *
 *   A paragraph with **bold**, *italic* and {red|coloured} text.
 *
 *   - a bullet
 *   - another
 *
 * It stays plain text so the prompt, the examples and the history all read it as written; the editor
 * converts it to and from draft-js content.
 */

export type MarkColor = 'green' | 'red' | 'blue' | 'grey';
const MARK_COLORS: MarkColor[] = ['green', 'red', 'blue', 'grey'];

export type Span = { text: string; bold?: boolean; italic?: boolean; color?: MarkColor };
export type Block = { kind: 'paragraph'; spans: Span[] } | { kind: 'list'; items: Span[][] };
export type Commentary = { headline: Span[] | null; blocks: Block[] };

type Style = Omit<Span, 'text'>;

// **bold** · {red|coloured} · *italic* · _italic_
const INLINE =
    /\*\*(.+?)\*\*|\{(green|red|blue|grey)\|([^{}]+?)\}|(?<!\*)\*(?![\s*])(.+?)(?<![\s*])\*(?!\*)|(?<!\w)_(?!\s)(.+?)(?<!\s)_(?!\w)/g;
const BULLET = /^\s*[-•*]\s+(.*)$/;

export function parseInline(text: string, style: Style = {}): Span[] {
    const spans: Span[] = [];
    const pattern = new RegExp(INLINE.source, 'g');
    let last = 0;
    for (let m = pattern.exec(text); m; m = pattern.exec(text)) {
        if (m.index > last) spans.push({ ...style, text: text.slice(last, m.index) });
        if (m[1] !== undefined) spans.push(...parseInline(m[1], { ...style, bold: true }));
        else if (m[2] !== undefined) spans.push(...parseInline(m[3], { ...style, color: m[2] as MarkColor }));
        else spans.push(...parseInline(m[4] ?? m[5], { ...style, italic: true }));
        last = m.index + m[0].length;
    }
    if (last < text.length) spans.push({ ...style, text: text.slice(last) });
    return spans;
}

/** A first line starting "# " is the headline; paragraphs are split on blank lines, "- " lines are bullets. */
export function parseCommentary(text: string): Commentary {
    const lines = text.split('\n');
    const firstIndex = lines.findIndex((l) => l.trim() !== '');
    let headline: Span[] | null = null;
    if (firstIndex >= 0 && lines[firstIndex].trim().startsWith('# ')) {
        headline = parseInline(lines[firstIndex].trim().slice(2).trim());
        lines.splice(0, firstIndex + 1);
    }

    const blocks: Block[] = [];
    let paragraph: string[] = [];
    const closeParagraph = () => {
        if (paragraph.length > 0) blocks.push({ kind: 'paragraph', spans: parseInline(paragraph.join(' ')) });
        paragraph = [];
    };
    for (const raw of lines) {
        const line = raw.trim();
        const bullet = BULLET.exec(raw);
        if (line === '') {
            closeParagraph();
        } else if (bullet) {
            closeParagraph();
            const previous = blocks[blocks.length - 1];
            const item = parseInline(bullet[1].trim());
            if (previous?.kind === 'list') previous.items.push(item);
            else blocks.push({ kind: 'list', items: [item] });
        } else {
            // headings are not part of the format past the headline; keep the words
            paragraph.push(line.replace(/^#+\s+/, ''));
        }
    }
    closeParagraph();
    return { headline, blocks };
}

/** Plain words, for a one-line summary. */
export function plainText(spans: Span[]): string {
    return spans.map((s) => s.text).join('');
}

// --- draft-js ---------------------------------------------------------------------------------------

/** the editor's inline style for each colour; the colours themselves are in CommentaryText */
export const COLOR_STYLES: Record<MarkColor, string> = {
    green: 'COMMENTARY_GREEN',
    red: 'COMMENTARY_RED',
    blue: 'COMMENTARY_BLUE',
    grey: 'COMMENTARY_GREY',
};
const STYLE_COLORS = Object.fromEntries(MARK_COLORS.map((c) => [COLOR_STYLES[c], c])) as Record<string, MarkColor>;

let keySeed = 0;
const nextKey = () => `c${(keySeed++).toString(36)}`;

function toRawBlock(type: string, spans: Span[]): RawDraftContentBlock {
    const inlineStyleRanges: RawDraftInlineStyleRange[] = [];
    let text = '';
    let offset = 0;
    for (const span of spans) {
        // draft counts offsets in code points
        const length = Array.from(span.text).length;
        const styles = [
            span.bold ? 'BOLD' : null,
            span.italic ? 'ITALIC' : null,
            span.color ? COLOR_STYLES[span.color] : null,
        ].filter((s): s is string => s !== null);
        for (const style of styles) {
            inlineStyleRanges.push({ offset, length, style } as RawDraftInlineStyleRange);
        }
        text += span.text;
        offset += length;
    }
    return { key: nextKey(), type, text, depth: 0, inlineStyleRanges, entityRanges: [], data: {} };
}

export function commentaryToRaw(text: string): RawDraftContentState {
    const { headline, blocks } = parseCommentary(text);
    const raw: RawDraftContentBlock[] = [];
    if (headline) raw.push(toRawBlock('header-one', headline));
    for (const block of blocks) {
        if (block.kind === 'paragraph') raw.push(toRawBlock('unstyled', block.spans));
        else raw.push(...block.items.map((item) => toRawBlock('unordered-list-item', item)));
    }
    if (raw.length === 0) raw.push(toRawBlock('unstyled', []));
    return { blocks: raw, entityMap: {} };
}

function styleOf(styles: Set<string>): Style {
    let color: MarkColor | undefined;
    styles.forEach((s) => (color = STYLE_COLORS[s] ?? color));
    return { bold: styles.has('BOLD') || undefined, italic: styles.has('ITALIC') || undefined, color };
}

/** markers hug the words; spaces stay outside */
function wrap(text: string, open: string, close: string): string {
    const [, lead, inner, trail] = /^(\s*)(.*?)(\s*)$/s.exec(text)!;
    return inner === '' ? text : lead + open + inner + close + trail;
}

const LEVELS = ['bold', 'color', 'italic'] as const;

/** Bold outermost, then colour, then italic, each marker around the longest run it covers. */
function writeSpans(spans: Span[], level = 0, inBold = false): string {
    if (level === LEVELS.length) return spans.map((s) => s.text).join('');
    const field = LEVELS[level];
    const groups: Span[][] = [];
    spans.forEach((span, i) => {
        if (i > 0 && spans[i - 1][field] === span[field]) groups[groups.length - 1].push(span);
        else groups.push([span]);
    });
    return groups
        .map((group) => {
            const value = group[0][field];
            const inner = writeSpans(group, level + 1, inBold || (field === 'bold' && !!value));
            if (!value) return inner;
            if (field === 'bold') return wrap(inner, '**', '**');
            if (field === 'italic') return inBold ? wrap(inner, '_', '_') : wrap(inner, '*', '*');
            return /[{}]/.test(inner) ? inner : wrap(inner, `{${value}|`, '}');
        })
        .join('');
}

function fromRawBlock(block: RawDraftContentBlock): string {
    const chars = Array.from(block.text);
    const styles = chars.map(() => new Set<string>());
    for (const range of block.inlineStyleRanges) {
        for (let i = range.offset; i < Math.min(range.offset + range.length, chars.length); i++) {
            styles[i].add(range.style);
        }
    }
    const spans: Span[] = [];
    let key = '';
    chars.forEach((char, i) => {
        const style = styleOf(styles[i]);
        const next = JSON.stringify(style);
        if (spans.length > 0 && next === key) spans[spans.length - 1].text += char;
        else spans.push({ ...style, text: char });
        key = next;
    });
    return writeSpans(spans).trim();
}

export function rawToCommentary(raw: RawDraftContentState): string {
    const parts: string[] = [];
    let previousWasItem = false;
    raw.blocks.forEach((block, i) => {
        const text = fromRawBlock(block);
        if (text === '') return;
        const isItem = block.type === 'unordered-list-item' || block.type === 'ordered-list-item';
        const line = isItem ? `- ${text}` : i === 0 && block.type === 'header-one' ? `# ${text}` : text;
        if (parts.length > 0) parts.push(isItem && previousWasItem ? '\n' : '\n\n');
        parts.push(line);
        previousWasItem = isItem;
    });
    return parts.join('');
}
